import { createContext, useContext, useState, type PropsWithChildren } from 'react'

export type AppRole = 'admin' | 'field_supervisor' | 'export_handler' | 'brand'

export type RoleConfig = {
  label:              string
  description:        string
  badge:              string
  canCreate:          boolean
  canCreateProduct:   boolean
  canDelete:          boolean
  canUpdateDpp:       boolean   // Field Supervisor and admin
  canUpdateExportData: boolean  // Export Handler and admin
}

export const ROLES: Record<AppRole, RoleConfig> = {
  admin: {
    label:               'Admin',
    description:         'Full access',
    badge:               'bg-purple-100 text-purple-800 border-purple-200',
    canCreate:           true,
    canCreateProduct:    true,
    canDelete:           true,
    canUpdateDpp:        true,
    canUpdateExportData: true,
  },
  field_supervisor: {
    label:               'Field Supervisor',
    description:         'Create batches & fill raw material data',
    badge:               'bg-amber-100 text-amber-800 border-amber-200',
    canCreate:           true,
    canCreateProduct:    false,
    canDelete:           false,
    canUpdateDpp:        true,
    canUpdateExportData: false,
  },
  export_handler: {
    label:               'Export Handler',
    description:         'Add compliance & certification data',
    badge:               'bg-sky-100 text-sky-800 border-sky-200',
    canCreate:           false,
    canCreateProduct:    false,
    canDelete:           false,
    canUpdateDpp:        false,
    canUpdateExportData: true,
  },
  brand: {
    label:               'Brand / Retailer',
    description:         'View only',
    badge:               'bg-forest-100 text-forest-700 border-forest-200',
    canCreate:           false,
    canCreateProduct:    false,
    canDelete:           false,
    canUpdateDpp:        false,
    canUpdateExportData: false,
  },
}

export const ROLE_COLORS: Record<AppRole, string> = {
  admin:            '#6B21A8',
  field_supervisor: '#B45309',
  export_handler:   '#075985',
  brand:            '#1A6B3C',
}

type RoleContextValue = {
  role:    AppRole
  config:  RoleConfig
  setRole: (role: AppRole) => void
}

const RoleContext = createContext<RoleContextValue | null>(null)

const STORAGE_KEY = 'logistics_dpp_demo_role'

export function RoleProvider({ children }: PropsWithChildren) {
  const [role, setRoleState] = useState<AppRole>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    return (stored as AppRole) ?? 'admin'
  })

  const setRole = (r: AppRole) => {
    localStorage.setItem(STORAGE_KEY, r)
    setRoleState(r)
  }

  return (
    <RoleContext.Provider value={{ role, config: ROLES[role], setRole }}>
      {children}
    </RoleContext.Provider>
  )
}

export function useRole() {
  const ctx = useContext(RoleContext)
  if (!ctx) throw new Error('useRole must be used within RoleProvider')
  return ctx
}
